import React from 'react';
import { useReferral } from '../../contexts/ReferralContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { 
    Send, User, Calculator, Activity, HeartPulse, 
    Pill, Stethoscope, ClipboardList, FileText, AlertCircle
} from 'lucide-react';

const conditionLabels = {
    hip_arthritis: 'Hip Osteoarthritis',
    knee_arthritis: 'Knee Osteoarthritis',
    shoulder_arthritis: 'Shoulder Osteoarthritis',
    rotator_cuff_tear: 'Rotator Cuff Tear',
    impingement_syndrome: 'Impingement Syndrome',
    calcific_tendinitis: 'Calcific Tendinitis',
    ankle_arthritis: 'Ankle Osteoarthritis',
    ankle_impingement: 'Ankle Impingement',
    hallux_valgus: 'Hallux Valgus (Bunion)',
    spinal_stenosis: 'Spinal Stenosis',
    disc_herniation: 'Disc Herniation',
    carpal_tunnel_syndrome: 'Carpal Tunnel Syndrome',
    trigger_finger: 'Trigger Finger/Thumb',
    dupuytrens_contracture: "Dupuytren's Contracture",
    base_thumb_arthritis: 'Base of Thumb Arthritis',
    tennis_elbow: 'Tennis Elbow',
    golfers_elbow: "Golfer's Elbow",
    ulnar_nerve_compression: 'Ulnar Nerve Compression'
};

export default function ReviewSubmitStep({ onSubmit, isSubmitting }) {
    const { 
        patient, 
        bmi_data, 
        calculatedBMI,
        body_part, 
        condition,
        medical_history,
        medications,
        treatment_history,
        questionnaire_scores,
        clinical_findings,
        clinical_notes,
        urgency_justification
    } = useReferral();

    const SectionHeader = ({ icon: Icon, title }) => (
        <div className="flex items-center gap-2 mb-3">
            <Icon className="w-4 h-4 text-teal-600" />
            <h4 className="font-semibold text-slate-800">{title}</h4>
        </div>
    );

    const DataRow = ({ label, value, highlight = false }) => (
        <div className="flex justify-between py-1.5">
            <span className="text-slate-500 text-sm">{label}</span>
            <span className={`text-sm font-medium ${highlight ? 'text-teal-600' : 'text-slate-800'}`}>
                {value || '-'}
            </span>
        </div>
    );

    const getComorbidities = () => {
        const conditions = [];
        if (medical_history.diabetes) conditions.push(`Diabetes (${medical_history.diabetes_type || 'type unspecified'})`);
        if (medical_history.hypertension) conditions.push('Hypertension');
        if (medical_history.cardiac_disease) conditions.push(`Cardiac: ${medical_history.cardiac_details || 'Yes'}`);
        if (medical_history.respiratory_disease) conditions.push(`Respiratory: ${medical_history.respiratory_details || 'Yes'}`);
        if (medical_history.renal_disease) conditions.push('CKD');
        if (medical_history.liver_disease) conditions.push('Liver disease');
        if (medical_history.neurological_conditions) conditions.push(`Neurological: ${medical_history.neurological_details || 'Yes'}`);
        if (medical_history.autoimmune_conditions) conditions.push(`Autoimmune: ${medical_history.autoimmune_details || 'Yes'}`);
        if (medical_history.cancer_history) conditions.push(`Cancer: ${medical_history.cancer_details || 'Yes'}`);
        return conditions.length > 0 ? conditions.join(', ') : 'None recorded';
    };

    const getMedications = () => {
        const meds = [];
        if (medications.anticoagulants) meds.push(`Anticoagulant (${medications.anticoagulant_name || 'unspecified'})`);
        if (medications.steroids) meds.push('Long-term steroids');
        if (medications.immunosuppressants) meds.push('Immunosuppressants');
        if (medications.nsaids_regular) meds.push('Regular NSAIDs');
        if (medications.opioids_regular) meds.push('Regular opioids');
        return meds.length > 0 ? meds.join(', ') : 'None flagged';
    };

    const getTreatmentSummary = () => {
        const treatments = [];
        if (treatment_history.physiotherapy) {
            treatments.push(`Physiotherapy (${treatment_history.physio_sessions} sessions)`);
        }
        if (treatment_history.regular_analgesia) {
            treatments.push(`Analgesia (${treatment_history.analgesia_duration_months} months)`);
        }
        if (treatment_history.intra_articular_injections) {
            treatments.push(`Injections x${treatment_history.injection_count}`);
        }
        return treatments.length > 0 ? treatments.join(', ') : 'None recorded';
    };

    // Validation warnings
    const warnings = [];
    if (!patient.first_name || !patient.last_name) warnings.push('Patient name incomplete');
    if (!patient.date_of_birth) warnings.push('Date of birth missing');
    if (!condition) warnings.push('Condition not selected');
    if (!clinical_findings.affected_side) warnings.push('Affected side not specified');
    if (!clinical_findings.symptom_duration_months) warnings.push('Symptom duration not specified');

    return (
        <div className="space-y-6">
            {/* Validation Warnings */}
            {warnings.length > 0 && (
                <Card className="card-clinical border-amber-200 bg-amber-50">
                    <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-amber-900 mb-2">
                                    Please complete the following before submitting:
                                </h4>
                                <ul className="text-sm text-amber-700 space-y-1">
                                    {warnings.map((w, i) => (
                                        <li key={i}>• {w}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Review Card */}
            <Card className="card-clinical">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <FileText className="w-5 h-5 text-teal-600" />
                        Referral Summary
                    </CardTitle>
                    <CardDescription>
                        Review all information before submitting
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Patient Details */}
                    <div>
                        <SectionHeader icon={User} title="Patient Details" />
                        <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                            <DataRow label="Name" value={`${patient.first_name} ${patient.last_name}`} highlight />
                            <DataRow label="NHS Number" value={patient.nhs_number} />
                            <DataRow label="Date of Birth" value={patient.date_of_birth} />
                            <DataRow label="Gender" value={patient.gender} />
                            <DataRow label="Phone" value={patient.phone} />
                            <DataRow label="Postcode" value={patient.postcode} />
                        </div>
                    </div>

                    <Separator />

                    {/* BMI */}
                    <div>
                        <SectionHeader icon={Calculator} title="BMI Assessment" />
                        <div className="bg-slate-50 rounded-lg p-4">
                            {calculatedBMI ? (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            {calculatedBMI.bmi}
                                        </span>
                                        <span className="text-slate-500 ml-2">kg/m²</span>
                                    </div>
                                    <Badge className={
                                        calculatedBMI.category === 'normal' ? 'bg-emerald-100 text-emerald-700' :
                                        calculatedBMI.category.includes('obese') ? 'bg-rose-100 text-rose-700' :
                                        'bg-amber-100 text-amber-700'
                                    }>
                                        {calculatedBMI.category.replace(/_/g, ' ')}
                                    </Badge>
                                </div>
                            ) : (
                                <span className="text-slate-500">Not calculated</span>
                            )}
                        </div>
                    </div>

                    <Separator />

                    {/* Condition */}
                    <div>
                        <SectionHeader icon={Activity} title="Condition" />
                        <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                            <DataRow label="Body Part" value={body_part?.charAt(0).toUpperCase() + body_part?.slice(1)} />
                            <DataRow label="Diagnosis" value={conditionLabels[condition] || condition} highlight />
                            <DataRow label="Affected Side" value={clinical_findings.affected_side} />
                            <DataRow label="Duration" value={`${clinical_findings.symptom_duration_months} months`} />
                            <DataRow label="Functional Limitation" value={clinical_findings.functional_limitation} />
                        </div>
                    </div>

                    <Separator />

                    {/* Comorbidities */}
                    <div>
                        <SectionHeader icon={HeartPulse} title="Medical History" />
                        <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                            <p className="text-sm text-slate-700">{getComorbidities()}</p>
                            <DataRow label="Smoking" value={medical_history.smoking_status} />
                            <DataRow label="Alcohol" value={`${medical_history.alcohol_units_per_week} units/week`} />
                            {medical_history.allergies && (
                                <DataRow label="Allergies" value={medical_history.allergies} />
                            )}
                        </div>
                    </div>

                    <Separator />

                    {/* Medications */}
                    <div>
                        <SectionHeader icon={Pill} title="Relevant Medications" />
                        <div className="bg-slate-50 rounded-lg p-4">
                            <p className="text-sm text-slate-700">{getMedications()}</p>
                        </div>
                    </div>

                    <Separator />

                    {/* Treatment History */}
                    <div>
                        <SectionHeader icon={Stethoscope} title="Conservative Treatment" />
                        <div className="bg-slate-50 rounded-lg p-4">
                            <p className="text-sm text-slate-700">{getTreatmentSummary()}</p>
                            {treatment_history.intra_articular_injections && (
                                <DataRow 
                                    label="Last Injection" 
                                    value={treatment_history.last_injection_date || 'Date not specified'} 
                                />
                            )}
                        </div>
                    </div>

                    <Separator />

                    {/* Scores */}
                    <div>
                        <SectionHeader icon={ClipboardList} title="Assessment Scores" />
                        <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                            <DataRow label="VAS Pain Score" value={`${questionnaire_scores.vas_pain_score ?? '-'}/10`} />
                            {questionnaire_scores.oxford_hip_score !== null && (
                                <DataRow label="Oxford Hip Score" value={`${questionnaire_scores.oxford_hip_score}/48`} highlight />
                            )}
                            {questionnaire_scores.oxford_knee_score !== null && (
                                <DataRow label="Oxford Knee Score" value={`${questionnaire_scores.oxford_knee_score}/48`} highlight />
                            )}
                            {questionnaire_scores.odi_score !== null && (
                                <DataRow label="ODI Score" value={`${questionnaire_scores.odi_score}%`} highlight />
                            )}
                            {(questionnaire_scores.bss_red_flags || []).length > 0 && (
                                <div className="mt-2 p-2 bg-rose-100 rounded">
                                    <p className="text-sm text-rose-700 font-medium">
                                        Red Flags: {questionnaire_scores.bss_red_flags.length} identified
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Clinical Notes */}
                    {(clinical_notes || urgency_justification) && (
                        <>
                            <Separator />
                            <div>
                                <SectionHeader icon={FileText} title="Clinical Notes" />
                                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                                    {clinical_notes && (
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Summary</p>
                                            <p className="text-sm text-slate-700">{clinical_notes}</p>
                                        </div>
                                    )}
                                    {urgency_justification && (
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Urgency Justification</p>
                                            <p className="text-sm text-slate-700">{urgency_justification}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Submit Button */}
            <Card className="card-clinical bg-teal-50 border-teal-200">
                <CardContent className="pt-6">
                    <div className="text-center space-y-4">
                        <p className="text-sm text-teal-700">
                            By submitting this referral, it will be screened against BOA, NICE, and NHS guidelines.
                            You will receive immediate feedback on the referral outcome.
                        </p>
                        <Button
                            onClick={onSubmit}
                            disabled={isSubmitting || warnings.length > 0}
                            className="btn-primary px-12 py-6 text-lg"
                            data-testid="submit-referral-btn"
                        >
                            <Send className="w-5 h-5 mr-2" />
                            {isSubmitting ? 'Submitting...' : 'Submit Referral'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
